import type { ContentBlockT } from '@lotushealth/lotushealth_js_sdk/schemas';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { MemoizedMarkdown } from '../memoized-markdown';

export type ContentBlockRenderer = (block: ContentBlockT) => ReactNode;

export interface ContentBlocksProps extends Omit<ComponentProps<'div'>, 'children'> {
  blocks: readonly ContentBlockT[];
  renderersByType?: Partial<Record<string, ContentBlockRenderer>>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function getOptionalType(value: unknown): string | undefined {
  if (!isRecord(value)) return undefined;
  const type = value.type;
  return typeof type === 'string' ? type : undefined;
}

function renderContentBlockBody(
  block: ContentBlockT,
  renderersByType: ContentBlocksProps['renderersByType']
): ReactNode {
  const body = block.body;
  const type = getOptionalType(body);

  const customRenderer = type ? renderersByType?.[type] : undefined;
  if (customRenderer) {
    return customRenderer(block);
  }

  switch (type) {
    case 'text': {
      if (!isRecord(body)) return null;
      const text = body.text;
      if (typeof text !== 'string' || text.trim() === '') return null;
      return (
        <MemoizedMarkdown
          id={`content-block-${block.id}`}
          content={text}
          className="text-xs font-medium [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
        />
      );
    }

    case 'error': {
      if (!isRecord(body)) return null;
      const text = body.text;
      if (typeof text !== 'string' || text.trim() === '') return null;
      return (
        <div className="w-fit rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1 text-xs text-foreground">
          {text}
        </div>
      );
    }

    default:
      // Intentionally do not render unknown/unsupported blocks.
      return null;
  }
}

export function ContentBlocks({
  blocks,
  className,
  renderersByType,
  ...props
}: ContentBlocksProps) {
  const rendered = blocks.map((b) => ({
    id: b.id,
    node: renderContentBlockBody(b, renderersByType),
  }));
  const visible = rendered.filter((r) => r.node !== null);

  if (visible.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-col gap-2', className)} {...props}>
      {visible.map((r) => (
        <div key={r.id}>{r.node}</div>
      ))}
    </div>
  );
}
